'use client';

import { ColumnsType } from 'antd/es/table';
import {
  InputMaybe,
  SortOrderEnum,
  TransactionPage,
  TransactionsDownloadFilterInput,
  TransactionSortAttributeEnum,
  TransactionStats
} from 'gql/graphql';
import { createContext, Dispatch, FC, PropsWithChildren, SetStateAction, useContext } from 'react';
import { Control } from 'react-hook-form';
import useSearchParamsTransactions, { FiltersTransactionForm } from './useSearchParamsTransactions';
import useTableTransactions, {
  DataTransactionsTreeType,
  TRANSACTION_KEYS_MAP
} from './useTableTransactions';
type TTransactionsContext = {
  transactionStats?: Array<TransactionStats>;
  transactions?: TransactionPage;
  isLoading: boolean;
  isPending: boolean;
  isRetrieving: boolean;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  totalPages: number;
  handleClear: () => void;
  control: Control<FiltersTransactionForm>;
  filtersAdded: boolean;
  rows?: DataTransactionsTreeType[];
  columns: ColumnsType<DataTransactionsTreeType>;
  columnsKeys: string[];
  columnsKeysFiltered: string[];
  handleOnClickBadge: (key: string) => () => void;
  filters: TransactionsDownloadFilterInput;
  sortAttribute?: InputMaybe<TransactionSortAttributeEnum>;
  sortOrder?: InputMaybe<SortOrderEnum>;
  TRANSACTION_KEYS_MAP: typeof TRANSACTION_KEYS_MAP;
};

const initialState = {
  isLoading: false,
  isPending: false,
  isRetrieving: false,
  page: 1,
  setPage: () => {},
  totalPages: 0,
  handleClear: () => {},
  control: {} as Control<FiltersTransactionForm>,
  filtersAdded: false,
  rows: [],
  columns: [],
  columnsKeys: [],
  columnsKeysFiltered: [],
  handleOnClickBadge: () => () => {},
  filters: {} as TransactionsDownloadFilterInput,
  sortAttribute: undefined,
  sortOrder: undefined,
  TRANSACTION_KEYS_MAP: TRANSACTION_KEYS_MAP
};

const TransactionsContext = createContext<TTransactionsContext>(initialState);

const useTransactionsContext = (): TTransactionsContext => useContext(TransactionsContext);

const TransactionsProvider: FC<PropsWithChildren<unknown>> = ({ children }): JSX.Element => {
  const {
    transactionStats,
    isLoading,
    isPending,
    page,
    setPage,
    totalPages,
    transactions,
    isRetrieving,
    control,
    filtersAdded,
    handleClear,
    filters,
    sortAttribute,
    sortOrder
  } = useSearchParamsTransactions();

  const { rows, columns, columnsKeysFiltered, columnsKeys, handleOnClickBadge } =
    useTableTransactions({
      transactions
    });

  return (
    <TransactionsContext.Provider
      value={{
        transactionStats,
        transactions,
        isLoading,
        isPending,
        isRetrieving,
        page,
        setPage,
        totalPages,
        handleClear,
        control,
        filtersAdded,
        rows,
        columns,
        columnsKeysFiltered,
        columnsKeys,
        handleOnClickBadge,
        filters,
        sortAttribute,
        sortOrder,
        TRANSACTION_KEYS_MAP
      }}
    >
      {children}
    </TransactionsContext.Provider>
  );
};

export { TransactionsProvider, useTransactionsContext };
